import { AddIcon } from '@chakra-ui/icons';
import {
  Box,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  VStack,
} from '@chakra-ui/react';
import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd';
import { useCallback, useEffect, useRef, useState } from 'react';

import DevSandboxTooltip from 'components/DevSandbox/DevSandboxTooltip';
import Text from 'components/Text';

import PersonalInfoForm from './PersonalInfoForm';
import SectionForm from './SectionForm';
import { CVData, CVEntry, CVPersonalInfo, CVSectionType, CV_SECTION_DEFAULTS } from '../types';
import { createDefaultEntry, createDefaultSection } from '../utils/cvDefaults';
import { getAvailableSectionTypes } from '../utils/cvValidation';

interface CVFormProps {
  data: CVData;
  onChange: (data: CVData) => void;
  cvId?: string;
  showPhoto: boolean;
}

const PERSONAL_TAB_ID = '__personal__';

/** Section types with robust entry forms (title, dates, description) — show "add to top" button */
const ROBUST_SECTION_TYPES: CVSectionType[] = ['experience', 'education', 'projects', 'certifications'];

const TAB_LABELS: Record<string, string> = {
  [PERSONAL_TAB_ID]: 'About',
  experience: 'Experience',
  education: 'Education',
  skills: 'Skills',
  projects: 'Projects',
  certifications: 'Certifications',
  languages: 'Languages',
  hobbies: 'Hobbies',
  interests: 'Interests',
};

const DRAG_GRIP_ICON = (
  <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
    <circle cx="8" cy="4" r="2" />
    <circle cx="16" cy="4" r="2" />
    <circle cx="8" cy="12" r="2" />
    <circle cx="16" cy="12" r="2" />
    <circle cx="8" cy="20" r="2" />
    <circle cx="16" cy="20" r="2" />
  </svg>
);

const CVForm = ({ data, onChange, cvId, showPhoto }: CVFormProps) => {
  const [activeTab, setActiveTab] = useState(PERSONAL_TAB_ID);
  const [tabOrder, setTabOrder] = useState<string[]>([]);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const [shouldFocusNewEntry, setShouldFocusNewEntry] = useState(false);
  const [shouldFocusTopEntry, setShouldFocusTopEntry] = useState(false);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  // Sync tab order when sections change (add/remove)
  useEffect(() => {
    const sectionIds = data.sections.map((s) => s.id);

    setTabOrder((prev) => {
      // Filter out removed sections, keep existing order
      const existing = prev.filter((id) => id === PERSONAL_TAB_ID || sectionIds.includes(id));
      // Add new sections that aren't in the order yet
      const newIds = sectionIds.filter((id) => !existing.includes(id));
      // If empty (first render), initialize with saved aboutPosition
      if (existing.length === 0) {
        const pos = data.aboutPosition ?? 0;
        const order = [...sectionIds];
        order.splice(pos, 0, PERSONAL_TAB_ID);
        return order;
      }
      return [...existing, ...newIds];
    });
  }, [data.sections, data.aboutPosition]);

  // Auto-scroll to bottom after adding entry
  useEffect(() => {
    if (shouldScrollToBottom && contentScrollRef.current) {
      setTimeout(() => {
        contentScrollRef.current?.scrollTo({
          top: contentScrollRef.current.scrollHeight,
          behavior: 'smooth',
        });
        setShouldScrollToBottom(false);
        setShouldFocusNewEntry(true);
      }, 50);
    }
  }, [shouldScrollToBottom]);

  // Focus the first input of the newly added entry after scroll
  useEffect(() => {
    if (shouldFocusNewEntry && contentScrollRef.current) {
      setTimeout(() => {
        // Find all entry cards (Draggable wrappers), then focus first input of the last one
        const droppable = contentScrollRef.current?.querySelector('[data-rfd-droppable-id]');
        if (droppable) {
          const entryCards = droppable.children;
          const lastCard = entryCards[entryCards.length - 1];
          const firstInput = lastCard?.querySelector('input') as HTMLElement | null;
          firstInput?.focus();
        }
        setShouldFocusNewEntry(false);
      }, 150);
    }
  }, [shouldFocusNewEntry]);

  // Focus the first input of the newly prepended entry (top) after scroll
  useEffect(() => {
    if (shouldFocusTopEntry && contentScrollRef.current) {
      setTimeout(() => {
        const droppable = contentScrollRef.current?.querySelector('[data-rfd-droppable-id]');
        if (droppable) {
          const firstCard = droppable.children[0];
          const firstInput = firstCard?.querySelector('input') as HTMLElement | null;
          firstInput?.focus();
        }
        setShouldFocusTopEntry(false);
      }, 150);
    }
  }, [shouldFocusTopEntry]);

  // Scroll active tab into view (only horizontal, within container)
  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    // Small delay to ensure Draggable positions are settled
    requestAnimationFrame(() => {
      const activeEl = container.querySelector(`[data-tab-id="${activeTab}"]`) as HTMLElement | null;
      if (!activeEl) return;

      // Use getBoundingClientRect for reliable positioning regardless of nesting
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeEl.getBoundingClientRect();

      if (tabRect.left < containerRect.left) {
        container.scrollLeft += tabRect.left - containerRect.left;
      } else if (tabRect.right > containerRect.right) {
        container.scrollLeft += tabRect.right - containerRect.right;
      }
    });
  }, [activeTab]);

  const handlePersonalInfoChange = (field: keyof CVPersonalInfo, value: string | boolean) => {
    onChange({
      ...data,
      personalInfo: { ...data.personalInfo, [field]: value },
    });
  };

  const handleUpdateSectionTitle = (sectionId: string, title: string) => {
    onChange({
      ...data,
      sections: data.sections.map((s) => (s.id === sectionId ? { ...s, title } : s)),
    });
  };

  const handleUpdateEntry = (
    sectionId: string,
    entryId: string,
    field: keyof CVEntry,
    value: string | string[]
  ) => {
    onChange({
      ...data,
      sections: data.sections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              entries: s.entries.map((e) => (e.id === entryId ? { ...e, [field]: value } : e)),
            }
          : s
      ),
    });
  };

  const handleAddEntry = (sectionId: string) => {
    onChange({
      ...data,
      sections: data.sections.map((s) =>
        s.id === sectionId ? { ...s, entries: [...s.entries, createDefaultEntry()] } : s
      ),
    });
    setShouldScrollToBottom(true);
  };

  const handleAddEntryToTop = (sectionId: string) => {
    onChange({
      ...data,
      sections: data.sections.map((s) =>
        s.id === sectionId ? { ...s, entries: [createDefaultEntry(), ...s.entries] } : s
      ),
    });
    // Scroll to top and focus
    setTimeout(() => {
      contentScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      setShouldFocusTopEntry(true);
    }, 50);
  };

  const handleRemoveEntry = (sectionId: string, entryId: string) => {
    onChange({
      ...data,
      sections: data.sections.map((s) =>
        s.id === sectionId
          ? { ...s, entries: s.entries.filter((e) => e.id !== entryId) }
          : s
      ),
    });
  };

  const handleAddSection = useCallback(
    (type: CVSectionType) => {
      const newSection = createDefaultSection(type);
      onChange({
        ...data,
        sections: [...data.sections, newSection],
      });
      setActiveTab(newSection.id);
    },
    [data, onChange]
  );

  const handleReorderEntries = (sectionId: string, reorderedIds: string[]) => {
    onChange({
      ...data,
      sections: data.sections.map((s) => {
        if (s.id !== sectionId) return s;
        const entryMap = new Map(s.entries.map((e) => [e.id, e]));
        const reordered = reorderedIds.map((id) => entryMap.get(id)).filter(Boolean) as CVEntry[];
        return { ...s, entries: reordered };
      }),
    });
  };

  const handleRemoveSection = (sectionId: string) => {
    const sectionIndex = data.sections.findIndex((s) => s.id === sectionId);
    onChange({
      ...data,
      sections: data.sections.filter((s) => s.id !== sectionId),
    });
    if (activeTab === sectionId) {
      if (sectionIndex > 0) {
        setActiveTab(data.sections[sectionIndex - 1].id);
      } else {
        setActiveTab(PERSONAL_TAB_ID);
      }
    }
  };

  const usedTypes = data.sections.map((s) => s.type);
  const availableTypes = getAvailableSectionTypes(usedTypes);

  const handleDragEnd = (result: DropResult) => {
    // Delay reset so the click event doesn't fire immediately after drag
    setTimeout(() => { isDraggingRef.current = false; }, 100);
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const newOrder = Array.from(tabOrder);
    const [reordered] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, reordered);

    setTabOrder(newOrder);

    // Also reorder data.sections to match the new tab order (excluding PERSONAL_TAB_ID)
    const sectionOrder = newOrder.filter((id) => id !== PERSONAL_TAB_ID);
    const sectionMap = new Map(data.sections.map((s) => [s.id, s]));
    const reorderedSections = sectionOrder
      .map((id) => sectionMap.get(id))
      .filter(Boolean) as typeof data.sections;

    const aboutPosition = newOrder.indexOf(PERSONAL_TAB_ID);
    onChange({ ...data, sections: reorderedSections, aboutPosition });
  };

  const activeSection = data.sections.find((s) => s.id === activeTab);

  const renderTab = (id: string, index: number) => {
    const isPersonal = id === PERSONAL_TAB_ID;
    const section = isPersonal ? null : data.sections.find((s) => s.id === id);
    const tabLabel = isPersonal
      ? data.personalInfo.aboutTitle || 'About Me'
      : section
        ? section.title || TAB_LABELS[section.type] || 'Section'
        : 'Section';
    const isActive = activeTab === id;

    return (
      <Draggable key={id} draggableId={id} index={index}>
        {(draggableProvided, snapshot) => (
          <HStack
            ref={draggableProvided.innerRef}
            {...draggableProvided.draggableProps}
            {...draggableProvided.dragHandleProps}
            data-tab-id={id}
            px={3}
            height="100%"
            spacing={2}
            cursor="grab"
            onClick={() => { if (!isDraggingRef.current) setActiveTab(id); }}
            borderBottom="2px solid"
            borderColor={isActive ? 'purple.400' : 'transparent'}
            backgroundColor={
              snapshot.isDragging
                ? 'whiteAlpha.200'
                : isActive
                  ? 'whiteAlpha.50'
                  : 'transparent'
            }
            _hover={{ backgroundColor: 'whiteAlpha.50' }}
            transition={snapshot.isDragging ? 'none' : 'all 0.15s'}
            whiteSpace="nowrap"
            flexShrink={0}
            display="flex"
            alignItems="center"
            style={draggableProvided.draggableProps.style}
          >
            <Box
              color="whiteAlpha.300"
              display="flex"
              alignItems="center"
              flexShrink={0}
            >
              {DRAG_GRIP_ICON}
            </Box>
            <Text
              fontSize="small"
              fontWeight={isActive ? 'semibold' : 'regular'}
              opacity={isActive ? 'primary' : 'secondary'}
            >
              {tabLabel}
            </Text>
          </HStack>
        )}
      </Draggable>
    );
  };

  const renderTabBar = () => (
    <Box
      display="flex"
      borderBottom="1px solid"
      borderColor="whiteAlpha.100"
      backgroundColor="gray.800"
      flexShrink={0}
      height="42px"
      minHeight="42px"
      width="100%"
    >
      {/* Scrollable tabs area — left-aligned, horizontal scroll */}
      <Box
        ref={tabsContainerRef}
        flex={1}
        minWidth={0}
        overflowX="auto"
        overflowY="hidden"
        height="100%"
        css={{
          '&::-webkit-scrollbar': { height: '0px' },
          scrollbarWidth: 'none',
        }}
      >
        <DragDropContext onDragStart={() => { isDraggingRef.current = true; }} onDragEnd={handleDragEnd}>
          <Droppable droppableId="cv-tabs" direction="horizontal">
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                display="flex"
                alignItems="center"
                height="100%"
                width="max-content"
              >
                {tabOrder.map((id, index) => renderTab(id, index))}
                {provided.placeholder}

                {/* Add section + button — inline after last tab */}
                <Box
                  flexShrink={0}
                  display="flex"
                  alignItems="center"
                  height="100%"
                  backgroundColor="purple.800"
                  borderRadius="0 0 0 0"
                >
                  {availableTypes.length > 0 ? (
                    <Menu placement="bottom-end">
                      <DevSandboxTooltip label="Add section" placement="bottom">
                        <MenuButton
                          as={IconButton}
                          aria-label="Add section"
                          icon={<AddIcon boxSize={2.5} color="white" />}
                          variant="ghost"
                          size="xs"
                          h="100%"
                          w="36px"
                          minW="36px"
                          borderRadius={0}
                          _hover={{ backgroundColor: 'purple.700' }}
                        />
                      </DevSandboxTooltip>
                      <MenuList backgroundColor="gray.700" borderColor="whiteAlpha.200" minWidth="180px">
                        {availableTypes.map((type) => (
                          <MenuItem
                            key={type}
                            onClick={() => handleAddSection(type)}
                            backgroundColor="transparent"
                            _hover={{ backgroundColor: 'whiteAlpha.100' }}
                            fontSize="sm"
                          >
                            {CV_SECTION_DEFAULTS[type]}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  ) : (
                    <DevSandboxTooltip label="All section types added" placement="bottom">
                      <IconButton
                        aria-label="Add section (disabled)"
                        icon={<AddIcon boxSize={2.5} color="whiteAlpha.400" />}
                        variant="ghost"
                        size="xs"
                        h="100%"
                        w="36px"
                        minW="36px"
                        borderRadius={0}
                        isDisabled
                      />
                    </DevSandboxTooltip>
                  )}
                </Box>
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </Box>
  );

  const renderActiveContent = () => {
    if (activeTab === PERSONAL_TAB_ID) {
      return (
        <Box width="100%" height="100%" overflow="auto" px={4} py={4}>
          <PersonalInfoForm
            personalInfo={data.personalInfo}
            onChange={handlePersonalInfoChange}
            cvId={cvId}
            showPhoto={showPhoto}
          />
        </Box>
      );
    }

    if (!activeSection) {
      return (
        <Box px={4} py={8} textAlign="center">
          <Text fontSize="small" opacity="tertiary">
            Section not found. Select a tab above.
          </Text>
        </Box>
      );
    }

    return (
      <VStack spacing={0} width="100%" height="100%" overflow="hidden">
        <Box ref={contentScrollRef} flex={1} width="100%" overflow="auto" px={4} py={4}>
          <SectionForm
            section={activeSection}
            onUpdateTitle={(title) => handleUpdateSectionTitle(activeSection.id, title)}
            onUpdateEntry={(entryId, field, value) =>
              handleUpdateEntry(activeSection.id, entryId, field, value)
            }
            onRemoveEntry={(entryId) => handleRemoveEntry(activeSection.id, entryId)}
            onRemoveSection={() => handleRemoveSection(activeSection.id)}
            onReorderEntries={(ids) => handleReorderEntries(activeSection.id, ids)}
            onAddEntry={() => handleAddEntry(activeSection.id)}
            onAddEntryToTop={ROBUST_SECTION_TYPES.includes(activeSection.type)
              ? () => handleAddEntryToTop(activeSection.id)
              : undefined
            }
          />
        </Box>
        {/* Sticky footer: Add Entry — purple background */}
        <HStack
          as="button"
          width="100%"
          px={3}
          backgroundColor="purple.800"
          flexShrink={0}
          height="34px"
          minHeight="34px"
          justifyContent="center"
          spacing={1.5}
          cursor="pointer"
          _hover={{ backgroundColor: 'purple.700' }}
          transition="background-color 0.15s"
          onClick={() => handleAddEntry(activeSection.id)}
        >
          <AddIcon boxSize={2.5} color="white" />
          <Text fontSize="micro" fontWeight="semibold" letterSpacing="0.05em" color="white">
            Add Entry
          </Text>
        </HStack>
      </VStack>
    );
  };

  return (
    <VStack spacing={0} width="100%" height="100%" overflow="hidden">
      {renderTabBar()}
      <Box flex={1} width="100%" overflow="hidden">
        {renderActiveContent()}
      </Box>
    </VStack>
  );
};

export default CVForm;
